import _ from 'underscore';

let CDN = '/';

export function initCDN(CDN_) {
    CDN = CDN_;
}

import { map } from '@resn/gozer-math';

import loadTexture from './loaders/load-texture';
import loadCompressed from './loaders/load-compressed';
import loadJson from './loaders/load-json';
import loadImage from './loaders/load-image';
import loadAudio from './loaders/load-audio';
import loadGltf from './loaders/load-gltf';
import loadVideo from './loaders/load-video';

const screenWidth = window.screen.width;
const screenHeight = window.screen.height;
const maxDimension = Math.max(screenWidth, screenHeight);

const optimizedImageThresholds = [
    { size: 1440, ext: '' },
    { size: 960, ext: '@med' },
    { size: 0, ext: '@small' },
];

const compressedThresholds = [
    { size: 960, ext: '' },
    { size: 0, ext: '@small' },
];

const AssetsManager = {
    manifests: {},

    loaded: {},

    listeners: Object.create(null),

    loaders: {
        txt: loadTexture,
        txto: loadTexture,
        ct: loadCompressed,
        gltf: loadGltf,
        json: loadJson,
        img: loadImage,
        imgo: loadImage,
        audio: loadAudio,
        video: loadVideo,
    },

    preloadAsset: async ({ key, url, ...other }, manifest = 'core') => {
        const types = _.last(url.split('#')).split(',');
        const type = types[0];

        let filename = url;

        if (type === 'txto' || type === 'imgo') {
            const threshold = optimizedImageThresholds.find((threshold) => maxDimension >= threshold.size);

            const dir = filename.match(/(.*)[\/\\]/)[1] || '';
            const name = filename.replace(/^.*[\\\/]/, '');
            const splitName = name.split('.');

            // console.log('threshold', threshold.ext);
            // console.log('splitName', splitName);
            // console.log('filename', filename);
            // console.log('dir', dir);
            // console.log('name', name);

            filename = `${dir}/optimized/${splitName[0]}-optimized${threshold.ext}.${splitName[1]}`;
            // console.log('filename', filename);

            // let half = '';

            // if (types[1] && types[1] === 'half' && isHandHeld) {
            //     half = '@half';
            // }

            // filename = filename.split('.');
            // filename = `${filename[0]}-optimized${half}.${filename[1]}`;
        }

        if (type === 'ct') {
            const threshold = compressedThresholds.find((threshold) => maxDimension >= threshold.size);

            const dir = filename.match(/(.*)[\/\\]/)[1] || '';
            const name = filename.replace(/^.*[\\\/]/, '');
            const splitName = name.split('.');

            filename = `${dir}/basis/${splitName[0]}${threshold.ext}.basis`;
        }

        const data = await AssetsManager.loaders[type](CDN + filename);

        const asset = { ...other, key, filename, data };

        // add result to loaded object
        if (!AssetsManager.loaded[manifest]) AssetsManager.loaded[manifest] = {};
        AssetsManager.loaded[manifest][key] = data;

        // emit asset loaded events
        AssetsManager.emitAssetLoad(manifest, key);

        return asset;
    },

    preloadManifest: async (manifest, onProgress) => {
        // console.log('manifest', manifest);
        let total = AssetsManager.manifests[manifest].items.length;
        let count = 0;

        // has assets to load
        if (total > 0) {
            let assets = await Promise.all(
                AssetsManager.manifests[manifest].items.map((asset) => {
                    return AssetsManager.preloadAsset(asset, manifest).then((asset) => {
                        const context = AssetsManager.manifests[manifest].contexts[asset.contextIndex];
                        count++;

                        if (onProgress) {
                            onProgress(count / total);
                        }
                        if (context.props) context.onAssetsPreloaderProgress(count / total);
                        return asset;
                    });
                }),
            );

            // update assets list with data
            assets.forEach((asset) => {
                AssetsManager.manifests[manifest].contexts[asset.contextIndex].assets = {};
            });
            assets.forEach((asset) => {
                const { contexts } = AssetsManager.manifests[manifest];
                contexts[asset.contextIndex].assets[asset.key] = asset.data;
            });
        } else {
            // no assets to load
            if (onProgress) onProgress(1);
        }

        await Promise.all(
            AssetsManager.manifests[manifest].contexts.map(async (context) => {
                if (context.props) return await context.onAssetsPreloaderComplete();
            }),
        );

        // emit manifest load to all loaders
        AssetsManager.emitManifestLoad(manifest);

        // delete loader manifest from store
        delete AssetsManager.manifests[manifest];
    },

    preloadAll: (onProgress) => {
        return new Promise((resolve) => {
            const loaderEntries = Object.entries(AssetsManager.manifests);

            if (loaderEntries.length > 0) {
                // total of manifests
                const total = loaderEntries.length;

                // manifests loaded
                // let count = 0;

                Promise.all(
                    loaderEntries.map(async ([manifest]) => {
                        return AssetsManager.preloadManifest(manifest, (progress) => {
                            // if (progress >= 1) count++;

                            if (onProgress) {
                                onProgress(map(progress * total, 0, total, 0, 1).toFixed(2));
                            }
                        });
                    }),
                ).then(() => {
                    resolve();
                });
            } else {
                if (onProgress) onProgress(1);

                setTimeout(resolve, 0);
            }
        });
    },

    /**
     * Manifest Events
     */

    onManifestLoad: (manifest, callback) => {
        AssetsManager.listeners[manifest] = AssetsManager.listeners[manifest] || [];
        AssetsManager.listeners[manifest].push(callback);
    },

    offManifestLoad: (manifest, callback) => {
        if (AssetsManager.listeners[manifest]) {
            AssetsManager.listeners[manifest].splice(AssetsManager.listeners[manifest].indexOf(callback) >>> 0, 1);
        }
    },

    emitManifestLoad: (manifest) => {
        (AssetsManager.listeners[manifest] || []).slice().map((handler) => {
            handler(manifest);
            return handler;
        });
    },

    /**
     * Assets Events
     */

    onAssetLoad: (manifest, asset, callback) => {
        if (AssetsManager.loaded[manifest] && AssetsManager.loaded[manifest][asset]) {
            callback();
            return;
        }

        AssetsManager.listeners[`${manifest}__${asset}`] = AssetsManager.listeners[`${manifest}__${asset}`] || [];
        AssetsManager.listeners[`${manifest}__${asset}`].push(callback);
    },

    offAssetLoad: (manifest, asset, callback) => {
        if (AssetsManager.listeners[`${manifest}__${asset}`]) {
            AssetsManager.listeners[`${manifest}__${asset}`].splice(
                AssetsManager.listeners[`${manifest}__${asset}`].indexOf(callback) >>> 0,
                1,
            );
        }
    },

    emitAssetLoad: (manifest, asset) => {
        (AssetsManager.listeners[`${manifest}__${asset}`] || []).slice().map((handler) => {
            handler();
            return handler;
        });
    },

    getAsset(manifest, assetName) {
        if (AssetsManager.loaded[manifest] && AssetsManager.loaded[manifest][assetName]) {
            return AssetsManager.loaded[manifest][assetName];
        } else {
            console.log('[assets-manager]', `Asset ${assetName} not found in ${manifest}.`);
        }
    },
};

if (__DEV__) window.AssetsManager = AssetsManager;

export default AssetsManager;
